import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Promise from 'bluebird';
import type { UAPPlacementComponentProps } from '../@types';

const AD_PREVIEW_SDK_URL = 'https://adsdk.microsoft.com/ad-preview/sdk.js';


export const getN2dScaleToFit = (adWidth: number, adHeight: number, maxWidth: number, maxHeight: number) =>
  Math.min(maxWidth / adWidth, maxHeight / adHeight);

// Singleton SDK loading — shared across all instances

let sdkPromise: Promise<void> | null = null;

export function preloadN2dAdPreviewSdk() {
  if (!sdkPromise) {
    sdkPromise = new Promise<void>((resolve, reject) => { // eslint-disable-line no-restricted-globals
      if (window.AdPreview) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = AD_PREVIEW_SDK_URL;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => {
        sdkPromise = null; // allow retry
        reject(new Error('Failed to load AdPreview SDK'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}

/*
 * Lightweight N2D ad renderer that loads the AdPreview SDK and renders
 * an ad into a container div, scaled to fit within maxWidth × maxHeight.
 *
 * This is the rendering core extracted from n2d-card-preview.jsx — no card
 * chrome, no header, no toggle, no page view. Designed for inline carousel use.
 */
const N2dAdRenderer = ({
  adWidth,
  adHeight,
  imageUrl,
  imageWidth: imageWidthProp,
  imageHeight: imageHeightProp,
  previewTexts,
  maxWidth,
  maxHeight,
  i18n,
}: UAPPlacementComponentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const [renderComplete, setRenderComplete] = useState(false);
  const [isVisible, setIsVisible] = useState(typeof IntersectionObserver === 'undefined');

  // Use actual image dimensions if provided, otherwise fall back to ad dimensions
  const imageWidth = imageWidthProp || adWidth;
  const imageHeight = imageHeightProp || adHeight;

  // Load SDK once
  useEffect(() => {
    preloadN2dAdPreviewSdk()
      .then(() => setSdkReady(true))
      .catch(() => setSdkError(true));
  }, []);

  useEffect(() => {
    if (isVisible || !hostRef.current || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    });
    observer.observe(hostRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Scale in either direction to maximize the preview within maxWidth ×
  // maxHeight while preserving its aspect ratio. This keeps wide/tall formats
  // bounded and enlarges smaller formats to use the available carousel area.
  const scale = getN2dScaleToFit(adWidth, adHeight, maxWidth, maxHeight);
  const scaledWidth = Math.round(adWidth * scale);
  const scaledHeight = Math.round(adHeight * scale);

  // Render ad when SDK is ready
  useEffect(() => {
    if (!sdkReady || !isVisible || !window.AdPreview || !containerRef.current) return undefined;

    const container = containerRef.current;
    container.innerHTML = '';
    setRenderComplete(false);
    const markRenderComplete = () => {
      if (container.childElementCount > 0) {
        setRenderComplete(true);
      }
    };
    const renderObserver = new MutationObserver(markRenderComplete);
    renderObserver.observe(container, { childList: true, subtree: true });

    try {
      const instance = new window.AdPreview(container);
      instance.render(
        { adWidth, adHeight },
        {
          image: { src: imageUrl, width: imageWidth, height: imageHeight },
          title: (previewTexts && previewTexts.longHeadline) || '',
          providerName: (previewTexts && previewTexts.businessName) || '',
          description: (previewTexts && previewTexts.description) || '',
          ctaText: (previewTexts && previewTexts.callToAction) || '',
          url: (previewTexts && previewTexts.url) || '',
          disclaimer: null,
        }
      );
      markRenderComplete();

      // Set container to original ad dimensions for the SDK
      container.style.width = `${adWidth}px`;
      container.style.height = `${adHeight}px`;
      container.style.boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.14), 0 0 2px 0 rgba(0, 0, 0, 0.12)';

      // Apply scale + center positioning (same pattern as n2d-card-preview.jsx)
      if (scale !== 1) {
        container.style.transform = `scale(${scale})`;
        container.style.transformOrigin = 'center center';
        container.style.position = 'absolute';
        container.style.left = '50%';
        container.style.top = '50%';
        container.style.marginLeft = `-${adWidth / 2}px`;
        container.style.marginTop = `-${adHeight / 2}px`;
      }
    } catch (err) {
      setRenderComplete(false);
      setSdkError(true);
    }

    return () => {
      renderObserver.disconnect();
      container.innerHTML = '';
    };
  }, [sdkReady, isVisible, adWidth, adHeight, imageUrl, imageWidth, imageHeight, previewTexts, scale]);

  if (sdkError) {
    return (
      <div
        ref={hostRef}
        data-n2d-state="error"
        style={{
          width: scaledWidth,
          height: scaledHeight,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px dashed #d2d0ce',
          borderRadius: '4px',
          color: '#999',
          fontSize: '13px',
        }}
      >
        {i18n ? i18n.getString(_TL_('Failed to load preview')) : 'Failed to load preview'}
      </div>
    );
  }

  if (!sdkReady || !isVisible) {
    return (
      <div
        ref={hostRef}
        data-n2d-state="loading"
        style={{
          width: scaledWidth,
          height: scaledHeight,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          color: '#999',
          fontSize: '13px',
        }}
      >
        {i18n ? i18n.getString(_TL_('Loading...')) : 'Loading...'}
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      data-n2d-state={renderComplete ? 'ready' : 'rendering'}
      style={{
        width: scaledWidth,
        height: scaledHeight,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <div ref={containerRef} />
    </div>
  );
};

N2dAdRenderer.propTypes = {
  adWidth: PropTypes.number.isRequired,
  adHeight: PropTypes.number.isRequired,
  imageUrl: PropTypes.string.isRequired,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  previewTexts: PropTypes.shape({
    longHeadline: PropTypes.string,
    businessName: PropTypes.string,
    description: PropTypes.string,
    callToAction: PropTypes.string,
    url: PropTypes.string,
  }),
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }),
};

N2dAdRenderer.defaultProps = {
  previewTexts: {},
  imageWidth: undefined,
  imageHeight: undefined,
  maxWidth: 480,
  maxHeight: 350,
  i18n: null,
};

export { N2dAdRenderer };
